// @flow
import { Formik } from 'formik';
import React from 'react';
import { object } from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import { ConfirmModal } from '@presentational/modals/ConfirmModal';
import { toastError } from '@presentational/notifications/utils';
import { Spacer } from '@presentational/spacing/Spacer';
import { SPClusterTypeContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterTypeContainer';
import { SPClusterNameContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterNameContainer';
import { SPClusterProviderContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterProviderContainer';
import { SPClusterRegionsContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterRegionsContainer';
import { SPClusterNetworkingTypeContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterNetworkingTypeContainer';
import { SPClusterSlaContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterSLAContainer';
import { SPClusterAvailabilityConfigurationContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPClusterAvailabilityConfigurationContainer';
import { getLinkForOrganizationsPage } from '@streaming-projects/links';
import {
  useAddClusterResourceMutation,
  useUpdateClusterResourceMutation,
} from '@streaming-projects/service-definitions/streamingProjectsApi';
import { CLUSTER_RESOURCE_INPUTS_DETAILS_CONFIG } from '@streaming-projects/resource-definitions/cluster-resource-definitions/cluster-resource-inputs-config';
import {
  CLUSTER_RESOURCES_GRID_FIELDNAMES,
  CLUSTER_RESOURCES_GRID_HEADERS,
} from '@streaming-projects/orgs/enums';
import { ResourceEditRepriceWarningContainer } from '@streaming-projects/resource-definitions/auxilary-components/ResourceEditRepriceWarningContainer';
import { useMetaDataContext } from '@streaming-projects/orgs/contexts/SPMetaDataContextProvider';
import { SPDedicatedPerCKUPartitionLimitIncreaseToggleContainer } from '@streaming-projects/resource-definitions/cluster-resource-definitions/SPDedicatedPerCKUPartitionLimitIncreaseToggleContainer';
import useIsAdmin from '@streaming-projects/useIsAdmin';
import { shouldShowDedicatedPerCKUPartitionLimitIncreaseToggleOption } from '@streaming-projects/utils';
import { IS_DEDICATED_PARTITIONS_LIMIT_EXTENSION_LIVE } from '@streaming-projects/constants';

import { getValidationSchemaFromColsConfig } from '../../../../configuration/utils';
import { StyledContainer } from '../../../../common-utils/styledComponents';

import { getClusterResouceDefaultInputValues } from './utils';

const getAddAndUpdateParams = (orgId, initialValues, addClusterFormik) => {
  // Note, clusterResourceId and Version are only applicable in Editing Mode and hence the usage of "?."
  return {
    orgId,
    clusterResourceId: initialValues?.[CLUSTER_RESOURCES_GRID_HEADERS.ID],
    payload: {
      org_id: orgId,
      name: addClusterFormik.values[CLUSTER_RESOURCES_GRID_FIELDNAMES.NAME],
      inputs: {
        ...addClusterFormik.values,
      },
      version: initialValues?.[CLUSTER_RESOURCES_GRID_HEADERS.VERSION],
    },
  };
};

export const SPClusterResourceConfigurationAddEditModal = ({
  isOpen,
  setOpen,
  isEditingMode,
  initialValues = null,
}) => {
  const { orgId } = useParams();
  const { cluster_resource_default_config: clusterResourceDefaultInputValues } =
    useMetaDataContext();

  const [addClusterResource] = useAddClusterResourceMutation();
  const [updateClusterResource] = useUpdateClusterResourceMutation();
  const { push } = useHistory();
  const isUserAdmin = useIsAdmin();

  const initialValuesToUse = getClusterResouceDefaultInputValues({
    initialValues,
    clusterResourceDefaultInputValues,
    isEditingMode,
  });

  const validationSchema = object({
    ...getValidationSchemaFromColsConfig(CLUSTER_RESOURCE_INPUTS_DETAILS_CONFIG),
  });

  return (
    <StyledContainer>
      <Formik
        autoComplete="off"
        enableReinitialize={true}
        initialValues={initialValuesToUse}
        onSubmit={() => {}}
        validateOnMount={true}
        validationSchema={validationSchema}
      >
        {(addClusterFormik) => {
          return (
            <ConfirmModal
              body={
                <>
                  <Grid columns={3}>
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <SPClusterNameContainer disableOnFormErrors={false} fluid={true} />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={4}>
                        <SPClusterTypeContainer disableOnFormErrors={false} fluid={true} />
                      </Grid.Column>
                      <Grid.Column width={6}>
                        <SPClusterProviderContainer disableOnFormErrors={false} fluid={true} />
                      </Grid.Column>
                      <Grid.Column width={6}>
                        <SPClusterRegionsContainer disableOnFormErrors={false} fluid={true} />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={4}>
                        <SPClusterAvailabilityConfigurationContainer
                          disableOnFormErrors={false}
                          fluid={true}
                        />
                      </Grid.Column>
                      <Grid.Column width={6}>
                        <SPClusterSlaContainer disableOnFormErrors={false} fluid={true} />
                      </Grid.Column>
                      <Grid.Column width={6}>
                        <SPClusterNetworkingTypeContainer
                          disableOnFormErrors={false}
                          fluid={true}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={6}>
                        {IS_DEDICATED_PARTITIONS_LIMIT_EXTENSION_LIVE &&
                          shouldShowDedicatedPerCKUPartitionLimitIncreaseToggleOption(
                            addClusterFormik.values
                          ) && (
                            <SPDedicatedPerCKUPartitionLimitIncreaseToggleContainer
                              disableOnFormErrors={false}
                              disabled={!isUserAdmin}
                              fluid={true}
                            />
                          )}
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Spacer y={20} />
                  {isEditingMode && <ResourceEditRepriceWarningContainer resourceType="Cluster" />}
                </>
              }
              cancelButtonNegative={true}
              disabled={!addClusterFormik.isValid}
              header={
                isEditingMode
                  ? 'Edit Cluster Resource Configuration'
                  : 'Add New Cluster Resource Configuration'
              }
              isOpen={isOpen}
              okButtonNegative={false}
              okButtonText="Continue"
              onClickHandlerForCancel={() => {
                addClusterFormik.resetForm();
                return setOpen(false);
              }}
              onClickHandlerForOK={async () => {
                let errorInCreateOrUpdate;
                if (!isEditingMode) {
                  const { error } = await addClusterResource(
                    getAddAndUpdateParams(orgId, initialValues, addClusterFormik)
                  );
                  errorInCreateOrUpdate = error;
                } else {
                  const { error } = await updateClusterResource(
                    getAddAndUpdateParams(orgId, initialValues, addClusterFormik)
                  );
                  errorInCreateOrUpdate = error;
                }
                if (errorInCreateOrUpdate) {
                  toastError(errorInCreateOrUpdate);
                } else {
                  push(getLinkForOrganizationsPage(orgId));
                }
                addClusterFormik.resetForm();
                setOpen(false);
              }}
            />
          );
        }}
      </Formik>
    </StyledContainer>
  );
};
